import React from "react"
import { navigate } from "gatsby"

import Airtable from "airtable"

import * as talkToUsStyles from "../styles/TalkToUs.module.css"

import { ChildSelector } from "../helpers/ChildSelector"

import TwoColumnHeroWithImageRight, {
  CONTAINMENT_MODE,
} from "./_base/TwoColumnHeroWithImageRight"

// We don't get this from AirTable only so that the form can be submitted if something fails in the loading process
const cubewiseRegions = [
  `Africa`,
  `Australia - New South Wales and Queensland`,
  `Australia - Victoria, South Australia, Western Australia`,
  `Austria`,
  `Belgium`,
  `Netherlands`,
  `Canada`,
  `France`,
  `Germany`,
  `Hong Kong`,
  `India`,
  `People's Republic of China`,
  `Poland`,
  `Singapore`,
  `Switzerland`,
  `Taiwan`,
  `Türkiye`,
  `Ukraine`,
  `United Kingdom`,
  `United States - East`,
  `United States - Central`,
  `United States - West`,
  `Other`,
]

export default function TalkToUs(props) {
  const { content } = props

  const [, forceUpdate] = React.useReducer(x => x + 1, 0)

  const regions = React.useRef(null)

  const [isSubmitting, setSubmitting] = React.useState(false)
  const [isSubmitted, setSubmitted] = React.useState(false)
  const [hasError, setHasError] = React.useState(false)

  const [data, setData] = React.useState({
    email: ``,
    firstName: ``,
    lastName: ``,
    region: ``,
    message: ``,
    consulting: false,
    education: false,
    support: false,
    software: false,
    other: false,
    optIn: false,
  })

  //on page load, loads regions from Airtable
  React.useEffect(() => {
    async function loadRegions() {
      const base = new Airtable({
        apiKey: process.env.GATSBY_AIRTABLE_API_TOKEN,
      }).base("appV3kFhqEu7sRNwt")

      let data = []
      base("RegionMapping")
        .select({
          view: "Grid view",
          sort: [{ field: `region`, direction: `asc` }],
        })
        .eachPage(
          function page(records, fetchNextPage) {
            // This function (`page`) will get called for each page of records.

            records.forEach(function (record) {
              data.push({ region: record.fields.region, id: record.id })
            })

            // To fetch the next page of records, call `fetchNextPage`.
            // If there are more records, `page` will get called again.
            // If there are no more records, `done` will get called.
            fetchNextPage()
          },
          function done(err) {
            if (err) {
              console.error(err)
              regions.current = []
              return
            }

            regions.current = data
            forceUpdate()
          },
        )
    }

    if (!regions.current) loadRegions()
  }, [])

  const handleItemChange = evt => {
    let updated = data
    if (Object.keys(evt.target).includes(`checked`)) {
      updated[evt.target.name] = evt.target.checked
    } else {
      updated[evt.target.name] = evt.target.value
    }
    setData(updated, forceUpdate())
  }

  const handleSubmit = evt => {
    evt.preventDefault()

    setHasError(false)
    setSubmitting(true)

    const focusItems = [
      `consulting`,
      `education`,
      `support`,
      `software`,
      `other`,
    ]
    const focus = Object.keys(data)
      .filter(i => focusItems.includes(i) && data[i])
      .toString()

    const base = new Airtable({
      apiKey: process.env.GATSBY_AIRTABLE_API_TOKEN,
    }).base("appV3kFhqEu7sRNwt")

    let filteredData = JSON.parse(JSON.stringify(data))
    focusItems.forEach(k => {
      delete filteredData[k]
    })

    //get region ID for Airtbable API
    const regionId = regions.current.find(
      e => e.region === filteredData.region,
    ).id

    base("ContactRequests").create(
      [
        {
          fields: {
            ...filteredData,
            focus: focus,
            region: [regionId],
          },
        },
      ],
      function (err, records) {
        if (err) {
          console.error(err)
          setHasError(true)
          setSubmitting(false)

          return
        }
        records.forEach(function (record) {
          const submittedPage = record && record.fields[`thankYouContactingUs`]
          setSubmitted(true)
          setSubmitting(false)
          if (submittedPage) navigate(submittedPage)
        })
      },
    )
  }

  const title = ChildSelector.select(
    content,
    `talk-to-us-title`,
    ChildSelector.SELECTOR_TYPE.TEXT,
  )

  return (
    <>
      <div className={`row ${talkToUsStyles.wrapper}`}>
        <div className="col-12">
          <TwoColumnHeroWithImageRight
            contentClass={talkToUsStyles.content}
            imageHolder={content}
            imageSelector={`talk-to-us-media`}
            imageClass={`talk-to-us-illustration ${talkToUsStyles.backgroundImage}`}
            className={`icw-no-border ${talkToUsStyles.wrapper}`}
            mode={CONTAINMENT_MODE.normal}
          >
            <h2>{title}</h2>
            {!isSubmitted && (
              <form
                className={`${talkToUsStyles.content}`}
                onSubmit={handleSubmit}
              >
                <div className={`${talkToUsStyles.itemWrapper}`}>
                  <label htmlFor="email" className={`${talkToUsStyles.item}`}>
                    Email
                    <div className={`${talkToUsStyles.inputWrapper}`}>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        required
                        value={data.email}
                        onChange={handleItemChange}
                      />
                    </div>
                  </label>
                </div>
                <div className={`${talkToUsStyles.itemWrapper}`}>
                  <label
                    htmlFor="firstName"
                    className={`${talkToUsStyles.item}`}
                  >
                    First Name
                    <div className={`${talkToUsStyles.inputWrapper}`}>
                      <input
                        id="firstName"
                        type="text"
                        name="firstName"
                        required
                        value={data.firstName}
                        onChange={handleItemChange}
                      />
                    </div>
                  </label>
                </div>
                <div className={`${talkToUsStyles.itemWrapper}`}>
                  <label
                    htmlFor="lastName"
                    className={`${talkToUsStyles.item}`}
                  >
                    Last Name
                    <div className={`${talkToUsStyles.inputWrapper}`}>
                      <input
                        id="lastName"
                        type="text"
                        name="lastName"
                        required
                        value={data.lastName}
                        onChange={handleItemChange}
                      />
                    </div>
                  </label>
                </div>
                <div className={`${talkToUsStyles.itemWrapper}`}>
                  <label htmlFor="region" className={`${talkToUsStyles.item}`}>
                    Region
                    <div className={`${talkToUsStyles.inputWrapper}`}>
                      <select
                        value={data.region}
                        name="region"
                        required
                        onChange={handleItemChange}
                      >
                        <option value="" disabled defaultValue="">
                          Select a region
                        </option>
                        {cubewiseRegions.map((region, idx) => (
                          <option key={idx} value={region}>
                            {region}
                          </option>
                        ))}
                      </select>
                    </div>
                  </label>
                </div>
                <div className={`${talkToUsStyles.itemWrapper}`}>
                  <label htmlFor="message" className={`${talkToUsStyles.item}`}>
                    Message
                  </label>
                  <textarea
                    className="form-control"
                    id="message"
                    name="message"
                    value={data.message}
                    onChange={handleItemChange}
                    rows="3"
                  ></textarea>
                </div>
                <div className={`${talkToUsStyles.formWrapper}`}>
                  <label htmlFor="focus">
                    Where we should focus our conversation?
                  </label>
                  <span className={`${talkToUsStyles.filterSelector}`}>
                    <label>
                      <input
                        type="checkbox"
                        name="consulting"
                        value={data.consulting}
                        onChange={handleItemChange}
                      />
                      Consulting
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        name="education"
                        value={data.education}
                        onChange={handleItemChange}
                      />
                      Education
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        name="support"
                        value={data.support}
                        onChange={handleItemChange}
                      />
                      Support
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        name="software"
                        value={data.software}
                        onChange={handleItemChange}
                      />
                      Software
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        name="other"
                        value={data.other}
                        onChange={handleItemChange}
                      />
                      Other
                    </label>
                  </span>
                </div>
                <div className={`col-12 ${talkToUsStyles.formCheck}`}>
                  <input
                    id="optIn"
                    name="optIn"
                    className="form-check-input"
                    type="checkbox"
                    value={data.optIn}
                    onChange={handleItemChange}
                    required
                  />
                  <label htmlFor="optIn" className="form-check-label">
                    We would like your permission to reach you via email on the
                    latest news, advancements and best-practices in the FP&A and
                    TM1 development. We promise we won’t bother you more than
                    once a month.
                  </label>
                </div>
                <div className={`${talkToUsStyles.formSubmit}`}>
                {!isSubmitting && (
                  <button type="submit">Submit details</button>)}
                  {hasError && (
                    <p>An unexpected error has occurred. Please try again...</p>
                  )}
                </div>
              </form>
            )}
            {isSubmitted && (
              <>
                <h3>
                  <br />
                  <br />
                  Thank you for reaching out.
                  <br />
                  We will be in touch soon!
                </h3>
                <br />
                <br />
              </>
            )}
          </TwoColumnHeroWithImageRight>
        </div>
      </div>
    </>
  )
}
